<template>
    <b-modal
        id="student-internship-apply-modal"
        v-if="internship"
        v-model="modal"
        ref="student-internship-apply-modal"
        :title="internship.name"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        modal-class="apprentice-default-modal internship-detail-modal"
        :ok-title="$t('common.save')"
        size="lg"
        @ok="save">
        <template #modal-header="{ close }">
            <div class="header">
                <img
                    :alt="`Banner ${internship.name}`"
                    :src="internship.file ? internship.file.public_path : '/images/no-media.jpg'" />
                <b-button
                    class="btn-times btn-close"
                    variant="primary-icon-only"
                    @click="close()">
                </b-button>
            </div>
        </template>

        <div class="internship-details">
            <div class="internship-details-header">
                <div class="internship-title">
                    <h3 class="internship-text">{{ internship.name }}</h3>
                </div>
            </div>

            <div class="internship-details-content">
                <div class="additional-info">
                    <div
                        class="internship-type"
                        v-if="internship.internship_tags.length">
                        <span class="label">{{ $t('group.internship.internship_type') }}:</span>
                        <div class="tags-list">
                            <b-badge
                                class="tag"
                                v-for="tag in internship.internship_tags"
                                :key="tag.id"
                                pill
                                variant="primary">
                                {{ tag.name }}
                            </b-badge>
                        </div>
                    </div>

                    <div
                        class="internship-organization"
                        v-if="internship.organization">
                        <span class="label">{{ $t('common.organization') }}:</span>

                        <span class="organization">
                            {{ internship.organization }}
                        </span>
                    </div>

                    <div
                        class="internship-location"
                        v-if="internship.location">
                        <span class="label">{{ $t('common.location') }}:</span>

                        <span class="location">
                            <i class="fas fa-map-marker-alt gg-map-icon"></i>
                            {{ internship.location }}
                        </span>
                        <span
                            class="country"
                            v-if="internship.country">
                            {{ internship.country }}
                        </span>
                    </div>

                    <div
                        class="internship-email"
                        v-if="internship.email">
                        <span class="label">{{ $t('common.email_address') }}:</span>
                        <a :href="`mailto:${internship.email}`">{{ internship.email }}</a>
                    </div>

                    <div
                        class="internship-phone-number"
                        v-if="internship.phone">
                        <span class="label">{{ $t('common.phone_number') }}:</span>
                        <a :href="`tel:${internship.phone}`">{{ internship.phone }}</a>
                    </div>
                </div>
                <design-2021-apprentice-map-component
                    class="internship-details-google-map"
                    :address="getAddress"
                    :google-map-api="googleMapApi"
                    :title="internship.organization">
                </design-2021-apprentice-map-component>
            </div>
            <h4>{{ $t('common.description') }}</h4>
            <design-2021-apprentice-display-html-component
                class="internship-details-description"
                v-html="internship.description">
            </design-2021-apprentice-display-html-component>

            <div class="motivation">
                <h4 class="title">{{ $t('group.internship.motivation') }}</h4>
                {{ internship.pivot.motivation }}
            </div>
        </div>

        <div
            class="internship-approval"
            v-if="internship.pivot.status !== 0">
            <h5 class="title">{{ $t('group.internship.approval') }}</h5>

            <b-form-group :disabled="!$authUser.isTeacher() && !$authUser.isAdmin()">
                <b-form-radio-group
                    class="apprentice-radiobutton"
                    v-model="accepted"
                    :options="options"
                    name="radios-coordinator"
                    stacked></b-form-radio-group>
            </b-form-group>
        </div>

        <div class="internship-approval-feedback">
            <h5 class="title">Feedback</h5>

            <b-form-group>
                <b-form-textarea
                    class="apprentice-textarea"
                    id="textarea"
                    v-model="feedback"
                    :disabled="!$authUser.isTeacher() && !$authUser.isAdmin()"
                    :placeholder="$t('group.internship.post_your_feedback_here')"></b-form-textarea>
            </b-form-group>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'design-2021-group-show-internship-motivation-modal-component',
    props: {
        googleMapApi: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            modal: false,
            internship: null,
            accepted: 1,
            options: [
                { text: this.$t('group.internship.agree'), value: 2 },
                { text: this.$t('group.internship.re_reject'), value: 1 },
            ],
            feedback: null,
            mapConfig: {
                lat: 51.9886403,
                lng: 5.9345171,
            },
        };
    },
    methods: {
        show(internship) {
            this.internship = internship;
            this.accepted = internship.pivot.status !== 0 ? internship.pivot.status : 1;
            this.modal = true;
        },

        save() {
            this.alert.saving();
            if (!this.accepted && !this.feedback) {
                this.alert.error(this.$t('group.internship.feedback_required_in_case_of_rejection'));
                return;
            }

            let data = {
                projectId: this.internship.pivot.project_id,
                feedback: this.feedback,
            };

            switch (this.accepted) {
                case 1:
                    this.rejectInternship(data);
                    break;

                case 2:
                    this.acceptInternship(data);
                    break;
            }
        },

        acceptInternship(postData) {
            this.axios.post(this.routes.internshipAcceptStudent(), postData).then(
                (response) => {
                    this.$emit('accepted', response.data);
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        rejectInternship(deleteData) {
            this.axios.post(this.routes.internshipRejectStudent(), deleteData).then(
                (response) => {
                    this.$emit('rejected', response.data);
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
    },

    computed: {
        getAddress() {
            return {
                title: this.internship.organization,
                street: this.internship.street,
                number: this.internship.streetnumber,
                zipcode: this.internship.zipcode,
                location: this.internship.location,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
#student-internship-apply-modal___BV_modal_outer_ ::v-deep {
    .modal-dialog {
        .modal-content {
            .modal-header {
                position: relative;
                padding: 0;
                height: 175px;
                border-top-right-radius: $border-radius-content;
                border-top-left-radius: $border-radius-content;

                .btn-close {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    font-size: 1.5625rem;

                    &:before {
                        margin-right: 0;
                        font-weight: $font-weight-normal;
                    }
                }
            }
        }
    }
}

.header {
    width: 100%;
    height: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}

.internship-details-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    column-gap: $grid-gutter-width;
}

.internship-text {
    margin-bottom: map-get($grid-gutter-spacing, 'ten');
}

.additional-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .internship-tags ::v-deep {
        width: 100%;

        > div {
            width: inherit;
        }

        label {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
            min-width: 140px;
        }

        .apprentice-multiselect {
            min-width: 250px;
            max-width: 60%;
        }
    }

    > div {
        display: flex;
        flex-wrap: wrap;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        column-gap: map-get($grid-gutter-spacing, 'twenty-one');
        width: 100%;

        .label {
            margin: 0 0 map-get($grid-gutter-spacing, 'eight') 0;
            min-width: 140px;
            align-self: flex-start;
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }

    .gg-map-icon {
        color: var(--primary);
    }
}

.internship-details,
.internship-details-content,
.internship-details-description,
.internship-approval {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.internship-details-description {
    line-height: 1.3;
    margin-top: map-get($grid-gutter-spacing, 'ten');
}

.motivation {
    .title {
        color: $text-color;
    }

    color: $text-muted-color;
    font-weight: $font-weight-medium;
}

.internship-approval,
.internship-approval-feedback {
    .title {
        margin-bottom: map-get($grid-gutter-spacing, 'four');
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .additional-info {
        row-gap: map-get($grid-gutter-spacing, 'eight');

        > div {
            flex-direction: row;
            margin-bottom: 0;
            align-items: center;

            .label {
                margin: 0;
                align-self: center;
            }
        }

        .internship-tags ::v-deep {
            > div {
                width: calc(100% - 161px);
            }

            label {
                margin-bottom: 0;
            }

            .apprentice-multiselect {
                min-width: 100%;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .internship-details-header {
        flex-direction: row;

        .multiselect__tags {
            flex: 1 0 250px;
        }
    }

    .additional-info {
        .internship-tags ::v-deep {
            > div {
                width: calc(70% - 161px);
            }
        }
    }

    .internship-details-content {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: flex-start;
        column-gap: $grid-gutter-width;
    }

    .additional-info {
        width: calc(55% - 30px);

        > div:not(:last-child) {
            margin-bottom: 0;
        }
    }

    .internship-details-google-map {
        width: 45%;
    }
}
</style>
